import { CustomerInvoice, CustomerPayment } from '../../../clients/customer-api/invoice/customer-invoice';
import { useCallback, useEffect, useState } from 'react';
import { convertToMoney, displayMoneyFormat } from '../../../util/util';
import { PaymentMethodRequestV2 } from '../../../clients/quipli-service/payment/payment-v2';
import { PaymentMethodType } from '../../../clients/quipli-service/payment/payment';
import { InvoiceStatus, StatementPaymentSummary } from '../../../clients/quipli-service/invoice/invoice';
import { useCustomerPaymentApiV2 } from '../../../clients/customer-api/payment/customer-payment-api-v2';
import { Spinner } from '../../../components/spinner/spinner';
import { FiltersModal } from '../../../components/modal/filters-modal';
import { SelectPayment } from '../select-payment/select-payment';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import { useCustomer } from '../../../hooks/use-customer-context';
import { Currency } from 'dinero.js';
import { debounce } from 'lodash';
import { useCustomerAPI } from '../../../clients/customer-api/customer-api-client';
import { ecommerceToast } from '../../../util/toasts';

export const CustomerPaymentModal = ({
  isOpen,
  close,
  currency,
  balance,
  invoice,
}: {
  isOpen: boolean;
  close: (payments?: CustomerPayment[]) => void;
  currency: string;
  balance?: number;
  invoice?: CustomerInvoice;
}) => {
  const { createPayment } = useCustomerPaymentApiV2();
  const { getStatementPaymentSummary, getOrderById } = useCustomerAPI();

  const customer = useCustomer()!;

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodRequestV2 | undefined>();
  const [amount, setAmount] = useState<number>(invoice?.balance ?? balance ?? 0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCapturing, setIsCapturing] = useState<boolean>(false);
  const [statementSummary, setStatementSummary] = useState<StatementPaymentSummary[]>([]);
  const [error, setError] = useState<string>();
  const remainingBalance = invoice ? invoice?.balance - amount : 0;
  const formattedAmount = displayMoneyFormat(amount, currency);

  const getStatementSummary = useCallback(
    debounce(function () {
      getStatementPaymentSummary({ amount: amount, userId: customer.id })
        .then((summary) => {
          setError(undefined);
          setStatementSummary(summary);
        })
        .catch((e: any) => {
          console.log(e);
          setError(e.message);
        });
    }, 500),
    [amount],
  );

  useEffect(() => {
    getStatementSummary();
  }, [amount]);

  useEffect(() => {
    if (invoice?.type && !paymentMethod) {
      getOrderById(invoice.typeId).then((order) => {
        let pm: PaymentMethodRequestV2 | undefined = undefined;
        switch (order.paymentMethod.type) {
          case PaymentMethodType.Stripe:
            pm = {
              type: PaymentMethodType.Stripe,
              cardId: order.paymentMethod.card?.id,
              method: order.paymentMethod.method,
            };
            break;
          case PaymentMethodType.OnAccount:
          case PaymentMethodType.None:
          default:
            break;
        }
        setPaymentMethod(pm);
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [invoice, paymentMethod]);

  async function doPayment() {
    setIsCapturing(true);
    try {
      if (!paymentMethod) {
        ecommerceToast('Please select a payment method', 'error');
        return;
      }

      const result = await createPayment({
        invoiceIds: [
          ...(invoice?.id ? [invoice.id] : statementSummary.filter((_) => _.amount > 0).map((i) => i.invoiceId)),
        ],
        amount: amount,
        paymentMethod,
        userId: customer.id,
      });

      if (result.status === 'failed') {
        try {
          if (result.declineReason) {
            ecommerceToast(`Error: ${result.declineReason}`, 'error');
          } else {
            ecommerceToast('Error on payment', 'error');
          }
        } catch (ex: any) {
          ecommerceToast('Error on payment', 'error');
        }
      } else if (result.gatewayStatus === 'requires_action') {
        ecommerceToast('Payment requires authentication', 'error');
      } else {
        ecommerceToast('Payment Successful');
      }

      close([result]);
    } catch (ex: any) {
      ecommerceToast(ex.message, 'error');
    }
    setIsCapturing(false);
  }

  return (
    <FiltersModal
      isOpen={isOpen}
      title={invoice ? `Invoice #${invoice.invoiceNumber}` : `Make a Statement Payment`}
      confirmText={`Pay ${formattedAmount}`}
      cancelText="Cancel"
      allowConfirm={!isCapturing && !!paymentMethod && !error}
      allowCancel={!isCapturing}
      showConfirmButtonFirst={false}
      onConfirm={doPayment}
      onCancel={() => {
        close();
      }}
    >
      {invoice && (
        <div className="min-w-full mb-[24px] border border-solid border-[var(--grey-200)] rounded-lg flex flex-col gap-2">
          <div className="m-[8px] mb-[0] flex items-center justify-between">
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-800)]">
              Due Date
            </Typography>
            <Typography type="text-sm" weight="regular" className="text-[var(--grey-800)]">
              {invoice.dueDate?.toFormat('LLL-dd t') || 'N/A'}
            </Typography>
          </div>
          <div className="m-[8px] flex items-center justify-between">
            <Typography type="text-sm" weight="medium" className="text-[var(--grey-800)]">
              Balance Due
            </Typography>
            <Typography type="text-sm" weight="medium" className="text-[var(--grey-800)]">
              {invoice.status != InvoiceStatus.Void ? displayMoneyFormat(invoice.balance, currency) : '-'}
            </Typography>
          </div>
        </div>
      )}

      {!invoice && (
        <div className={'pb-10 gap-2 flex flex-column overflow-y-hidden max-h-[30%]'}>
          <Typography type={'text-lg'} weight={'semi-bold'}>
            Invoices
          </Typography>
          <div className={'rounded bg-gray-100 overflow-y-auto min-h-fit max-h-[30%] gap-2 p-2'}>
            {statementSummary.map((i) => (
              <Typography type={'text-sm'} key={i.invoiceId}>
                {i.invoiceNumber} - {convertToMoney(i.amount, currency as Currency).toFormat()} - {'Due: '}{' '}
                {convertToMoney(i.due, currency as Currency).toFormat()}
              </Typography>
            ))}
          </div>
        </div>
      )}
      <div className="min-w-full flex flex-col gap-2 items-stretch">
        <Typography type="text-xl">Make Payment</Typography>
        <div className="min-w-full h-[44px] p-[10px] border border-solid border-[var(--grey-300)] rounded-lg flex-[0_1_100%] flex items-center">
          <span className="ml-1 quipli-v2 text-md flex-[0_1_5%] ">$</span>
          <input
            type="number"
            step="0.01"
            className="appearance-textfield !outline-none !border-0 !border-none quipli-v2 text-md flex-[0_1_95%]"
            value={amount}
            onChange={(e) => setAmount(+e.target.value)}
          />
        </div>
        {error && (
          <Typography type="text-sm" weight="regular" className="text-[var(--error-500)]">
            {error}
          </Typography>
        )}
        {remainingBalance > 0 && (
          <div className="bg-[var(--error-50)] p-[16px] border border-solid border-[var(--error-300)] rounded-lg flex-[0_1_100%]">
            <Typography
              type="text-sm"
              weight="regular"
              className="text-[var(--error-500)] flex items-center justify-start gap-2"
            >
              <Icon icon="InfoCircle" stroke="var(--error-500)" size="large" />
              By paying {formattedAmount} you will still owe {displayMoneyFormat(remainingBalance, currency)}
            </Typography>
          </div>
        )}

        {!isLoading ? (
          <SelectPayment amount={amount} setPaymentMethod={setPaymentMethod} mode={'payment'}></SelectPayment>
        ) : (
          <Spinner />
        )}
      </div>
    </FiltersModal>
  );
};
